//
// src/pages/Login.js
//
// Secure multi-tenant login page.
//
// Important:
// - Login never creates a workspace.
// - Workspace codes must exactly match an existing registered tenant.
// - New workspaces can only be created through /register-tenant.
//

import {
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Link,
  Paper,
  Snackbar,
  TextField,
  Typography,
} from "@mui/material";

import {
  Link as RouterLink,
  useNavigate,
} from "react-router-dom";

import axios from "axios";


// =====================================================
// Configuration
// =====================================================

const RESOLVED_API_HOST = (
  process.env.REACT_APP_API_URL ||
  "http://localhost:8001"
).replace(/\/+$/, "");


const API_BASE_URL =
  RESOLVED_API_HOST.endsWith("/api")
    ? RESOLVED_API_HOST
    : `${RESOLVED_API_HOST}/api`;


const API_LOGIN =
  `${API_BASE_URL}/login`;


const LOGO_URL =
  "/logo.png";


const TENANT_CODE_PATTERN =
  /^[a-z][a-z0-9_]{2,49}$/;


const RESERVED_TENANT_CODES =
  new Set([
    "admin",
    "api",
    "api-pos",
    "information_schema",
    "pg_catalog",
    "pos",
    "postgres",
    "public",
    "www",
  ]);


// =====================================================
// Helpers
// =====================================================

function isIpAddress(hostname) {
  return (
    /^(\d{1,3}\.){3}\d{1,3}$/.test(hostname) ||
    hostname.includes(":")
  );
}


function detectSubdomainWorkspace() {
  const hostname =
    window.location.hostname
      .trim()
      .toLowerCase();

  if (
    !hostname ||
    hostname === "localhost" ||
    isIpAddress(hostname)
  ) {
    return null;
  }


  // Shared application hosts must never be treated
  // as tenant workspace codes.
  const sharedHosts =
    new Set([
      "aureviadigital.it.com",
      "www.aureviadigital.it.com",
      "pos.aureviadigital.it.com",
      "api.aureviadigital.it.com",
      "api-pos.aureviadigital.it.com",
    ]);


  if (sharedHosts.has(hostname)) {
    return null;
  }


  const parts =
    hostname
      .split(".")
      .filter(Boolean);


  // Optional future tenant-subdomain support:
  //
  // committedfaith.aureviadigital.it.com
  //        ↓
  // committedfaith
  //
  // aureviadigital.it.com itself has three labels,
  // therefore a tenant-prefixed hostname has at least four.
  if (parts.length >= 4) {
    const candidate =
      parts[0];


    if (
      candidate &&
      candidate !== "www" &&
      !RESERVED_TENANT_CODES.has(candidate)
    ) {
      return candidate;
    }
  }


  return null;
}


function validateWorkspaceCode(value) {
  if (!value) {
    return (
      "Company workspace code is required."
    );
  }


  if (value !== value.trim()) {
    return (
      "Workspace code cannot contain " +
      "leading or trailing spaces."
    );
  }


  if (value !== value.toLowerCase()) {
    return (
      "Workspace code must use lowercase letters only."
    );
  }


  if (!TENANT_CODE_PATTERN.test(value)) {
    return (
      "Workspace code must begin with a lowercase letter " +
      "and contain only lowercase letters, numbers, or underscores."
    );
  }


  if (RESERVED_TENANT_CODES.has(value)) {
    return (
      "This workspace code is reserved and cannot be used."
    );
  }


  return null;
}


function extractErrorMessage(error) {
  const statusCode =
    error?.response?.status;

  const detail =
    error?.response?.data?.detail;


  if (statusCode === 401) {
    return (
      typeof detail === "string"
        ? detail
        : "Invalid workspace code or credentials."
    );
  }


  if (statusCode === 403) {
    return (
      typeof detail === "string"
        ? detail
        : "Access denied. This account is inactive."
    );
  }


  if (statusCode === 422) {
    if (
      Array.isArray(detail) &&
      detail.length > 0
    ) {
      return (
        detail[0]?.msg ||
        "The submitted login information is invalid."
      );
    }


    return (
      typeof detail === "string"
        ? detail
        : "The submitted login information is invalid."
    );
  }


  if (
    typeof statusCode === "number" &&
    statusCode >= 500
  ) {
    return (
      "The server could not process the login request. " +
      "Please try again later."
    );
  }


  if (error?.request) {
    return (
      "Unable to contact the server. Check your network " +
      "connection and backend service."
    );
  }


  return (
    typeof detail === "string"
      ? detail
      : error?.message || "Login failed."
  );
}


function clearStaleAuthentication() {
  localStorage.removeItem("token");
  localStorage.removeItem("access_token");
  localStorage.removeItem("refresh_token");
  localStorage.removeItem("user");
  localStorage.removeItem("tenant_id");
  localStorage.removeItem("tenant_tier");

  sessionStorage.removeItem("token");
  sessionStorage.removeItem("access_token");
  sessionStorage.removeItem("user");
  sessionStorage.removeItem("tenant_id");
}


// =====================================================
// Component
// =====================================================

export default function Login({
  setLoggedUser,
}) {
  const navigate =
    useNavigate();


  const detectedWorkspace =
    useMemo(
      () => detectSubdomainWorkspace(),
      []
    );


  const [tenantId, setTenantId] =
    useState(
      detectedWorkspace || ""
    );


  const [
    usernameOrEmail,
    setUsernameOrEmail,
  ] = useState("");


  const [password, setPassword] =
    useState("");


  const [
    workspaceError,
    setWorkspaceError,
  ] = useState("");


  const [error, setError] =
    useState("");


  const [
    submitting,
    setSubmitting,
  ] = useState(false);


  const isDynamicTenant =
    Boolean(detectedWorkspace);


  // ===================================================
  // Validate detected workspace
  // ===================================================

  useEffect(() => {
    if (!detectedWorkspace) {
      return;
    }


    setWorkspaceError(
      validateWorkspaceCode(
        detectedWorkspace
      )
    );
  }, [detectedWorkspace]);


  // ===================================================
  // Input Handlers
  // ===================================================

  const handleWorkspaceChange =
    (event) => {
      const value =
        event.target.value;


      // Do not silently rewrite tenant codes.
      // Authentication should use exactly what
      // the user entered.
      setTenantId(value);


      if (workspaceError) {
        setWorkspaceError(
          validateWorkspaceCode(value)
        );
      }
    };


  const handleWorkspaceBlur = () => {
    setWorkspaceError(
      validateWorkspaceCode(
        tenantId
      )
    );
  };


  // ===================================================
  // Login
  // ===================================================

  const handleSubmit = async (event) => {
    event.preventDefault();


    if (submitting) {
      return;
    }


    setError("");


    const tenantValidationError =
      validateWorkspaceCode(
        tenantId
      );


    if (tenantValidationError) {
      setWorkspaceError(
        tenantValidationError
      );

      return;
    }


    setWorkspaceError("");


    const identity =
      usernameOrEmail.trim();


    if (!identity) {
      setError(
        "Username or email is required."
      );

      return;
    }


    if (!password) {
      setError(
        "Password is required."
      );

      return;
    }


    // Remove credentials from any previous session
    // before sending the new login request.
    clearStaleAuthentication();


    setSubmitting(true);


    try {
      const response =
        await axios.post(
          API_LOGIN,
          {
            username: identity,
            password,
          },
          {
            headers: {
              "Content-Type":
                "application/json",

              "X-Tenant-ID":
                tenantId,
            },

            timeout: 15000,
          }
        );


      const data =
        response.data;


      if (
        !data?.access_token ||
        !data?.user
      ) {
        throw new Error(
          "The server returned an incomplete login response."
        );
      }


      const authenticatedTenant =
        String(
          data.user.tenant_id || ""
        )
          .trim()
          .toLowerCase();


      if (!authenticatedTenant) {
        clearStaleAuthentication();

        throw new Error(
          "The server did not return an authenticated workspace."
        );
      }


      if (
        authenticatedTenant !==
        tenantId
      ) {
        clearStaleAuthentication();

        throw new Error(
          "The authenticated workspace does not match " +
          "the requested workspace."
        );
      }


      const loggedUser = {
        ...data.user,
        token: data.access_token,
      };


      localStorage.setItem(
        "token",
        data.access_token
      );


      localStorage.setItem(
        "access_token",
        data.access_token
      );


      if (data.refresh_token) {
        localStorage.setItem(
          "refresh_token",
          data.refresh_token
        );
      }


      localStorage.setItem(
        "tenant_id",
        authenticatedTenant
      );


      localStorage.setItem(
        "user",
        JSON.stringify(
          data.user
        )
      );


      if (data.user.tenant_tier) {
        localStorage.setItem(
          "tenant_tier",
          String(
            data.user.tenant_tier
          )
        );
      }


      if (setLoggedUser) {
        setLoggedUser(
          loggedUser
        );
      }


      const userRole =
        String(
          data.user.role || ""
        )
          .trim()
          .toLowerCase();


      let redirectTo =
        "/";


      if (
        userRole === "admin" ||
        userRole === "a"
      ) {
        redirectTo =
          "/users";
      } else if (
        userRole === "supervisor" ||
        userRole === "s"
      ) {
        redirectTo =
          "/products";
      } else if (
        userRole === "cashier" ||
        userRole === "c"
      ) {
        redirectTo =
          "/sales";
      }


      navigate(
        redirectTo,
        {
          replace: true,
        }
      );
    } catch (requestError) {
      clearStaleAuthentication();


      console.error(
        "[LOGIN_ERROR]",
        requestError
      );


      setError(
        extractErrorMessage(
          requestError
        )
      );
    } finally {
      setSubmitting(false);
    }
  };


  // ===================================================
  // Render
  // ===================================================

  return (
    <Container
      component="main"
      maxWidth="sm"
      sx={{
        py: {
          xs: 4,
          sm: 8,
        },
      }}
    >
      <Paper
        elevation={3}
        sx={{
          p: {
            xs: 3,
            sm: 4,
          },

          borderRadius: 2,
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            mb: 2,
          }}
        >
          <Box
            component="img"
            src={LOGO_URL}
            alt="FastAPIPOS by Aurevia Digital"
            sx={{
              width: "100%",
              maxWidth: 120,
              height: "auto",
              objectFit: "contain",
            }}
          />
        </Box>


        <Typography
          component="h1"
          variant="h5"
          sx={{
            textAlign: "center",
            mb: 1,
            fontWeight: 700,
          }}
        >
          Login
        </Typography>


        {isDynamicTenant && (
          <Alert
            severity={
              workspaceError
                ? "error"
                : "info"
            }
            sx={{
              mb: 2,
            }}
          >
            Workspace:{" "}
            <strong>
              {tenantId}
            </strong>

            {workspaceError && (
              <>
                <br />

                {workspaceError}
              </>
            )}
          </Alert>
        )}


        <Box
          component="form"
          onSubmit={handleSubmit}
          noValidate
        >
          {!isDynamicTenant && (
            <TextField
              fullWidth
              required
              margin="normal"
              label="Company Workspace Code"
              name="tenant_id"
              type="text"
              value={tenantId}
              onChange={handleWorkspaceChange}
              onBlur={handleWorkspaceBlur}
              error={
                Boolean(
                  workspaceError
                )
              }
              helperText={
                workspaceError ||
                (
                  "Enter the exact registered workspace code. " +
                  "Example: committedfaith"
                )
              }
              disabled={submitting}
              inputProps={{
                minLength: 3,
                maxLength: 50,
                autoCapitalize: "none",
                autoCorrect: "off",
                spellCheck: false,
              }}
              autoComplete="organization"
            />
          )}


          <TextField
            fullWidth
            required
            margin="normal"
            label="Username or Email"
            name="username"
            type="text"
            value={usernameOrEmail}
            onChange={(event) => {
              setUsernameOrEmail(
                event.target.value
              );
            }}
            disabled={submitting}
            inputProps={{
              maxLength: 255,
              autoCapitalize: "none",
              autoCorrect: "off",
              spellCheck: false,
            }}
            autoComplete="username"
          />


          <TextField
            fullWidth
            required
            margin="normal"
            label="Password"
            name="password"
            type="password"
            value={password}
            onChange={(event) => {
              setPassword(
                event.target.value
              );
            }}
            disabled={submitting}
            inputProps={{
              maxLength: 128,
            }}
            autoComplete="current-password"
          />


          <Button
            fullWidth
            type="submit"
            variant="contained"
            disabled={
              submitting ||
              Boolean(workspaceError)
            }
            sx={{
              mt: 3,
              py: 1.2,
              fontWeight: 700,
            }}
          >
            {submitting ? (
              <>
                <CircularProgress
                  size={20}
                  color="inherit"
                  sx={{
                    mr: 1,
                  }}
                />

                Signing In...
              </>
            ) : (
              "Sign In"
            )}
          </Button>


          <Box
            sx={{
              mt: 3,
              textAlign: "center",
            }}
          >
            <Typography
              variant="body2"
              color="text.secondary"
            >
              Need a new workspace for your business?{" "}

              <Link
                component={RouterLink}
                to="/register-tenant"
                fontWeight={600}
                color="primary"
                sx={{
                  textDecoration: "none",

                  "&:hover": {
                    textDecoration:
                      "underline",
                  },
                }}
              >
                Register Corporate Workspace
              </Link>
            </Typography>
          </Box>
        </Box>
      </Paper>


      <Snackbar
        open={Boolean(error)}
        autoHideDuration={6000}
        onClose={() => setError("")}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
      >
        <Alert
          severity="error"
          variant="filled"
          onClose={() => setError("")}
          sx={{
            width: "100%",
          }}
        >
          {error}
        </Alert>
      </Snackbar>
    </Container>
  );
}